import { Checkbox, FormControlLabel, FormHelperText, Stack } from '@mui/material';
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form';

import { TermsAndPrivacyLinks } from '@/features/identity/components/TermsAndPrivacyLinks';

export interface AcceptTermsCheckboxProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
}

/**
 * The consent gate on the registration form (ADR-0026). It lives in the Identity feature, beside
 * `RememberMeCheckbox`, because it is part of Identity's form — a feature must not import another
 * feature's internals (FrontendGuidelines §12), and this control needs nothing from the Legal slice
 * except the two public route paths. The Legal slice owns the documents themselves.
 *
 * <p>
 * The links open in a new tab (`target="_blank"`) on purpose: sending a half-filled registration form to
 * another route would lose everything typed so far, and a person who wants to read the terms before
 * agreeing is exactly the person whose form must survive the detour.
 * </p>
 */
export function AcceptTermsCheckbox<T extends FieldValues>({
  name,
  control,
}: AcceptTermsCheckboxProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={field.value === true}
                onChange={(event) => field.onChange(event.target.checked)}
                onBlur={field.onBlur}
                inputProps={{ 'aria-describedby': `${name}-error` }}
              />
            }
            label={
              <>
                I agree to the <TermsAndPrivacyLinks />.
              </>
            }
          />
          {fieldState.error ? (
            <FormHelperText id={`${name}-error`} error>
              {fieldState.error.message}
            </FormHelperText>
          ) : null}
        </Stack>
      )}
    />
  );
}
